/**
 * Central index / sector registry — single source of truth for the frontend.
 *
 * Every NSE/BSE index used anywhere in this application is defined here.
 * All scattered lookup maps (KITE_DISPLAY_NAMES, TICKER_MAP, toAssetId,
 * DISPLAY_TO_KITE_SYMBOL) are auto-generated from this single array.
 *
 * To add a new index: add one entry here. Nothing else needs to change.
 *
 * Fields:
 *   id             — matches backend RS/RRG id (e.g. 'niftyit')
 *   label          — canonical display name used in side-panel, RS tab, RRG
 *   kiteSymbol     — NSE/Kite trading symbol (what openPanel receives)
 *   category       — 'Index' | 'Broad' | 'Sector' | 'Thematic'
 *   inRRG          — appears in Sector Rotation RRG chart
 *   rrgTicker      — short ticker on RRG canvas (defaults to kiteSymbol stripped)
 *   sectorName     — Dashboard heatmap / PerformanceTab display name for Sector entries
 *                    (e.g. 'Automobile' for NIFTY Auto) — overrides label where different
 *   kiteShortLabel — key used in backend KITE_INDICES map (kite_price_service.py)
 *                    e.g. 'SMALLCAP250', 'NIFTYFIN'. Enables Dashboard to derive
 *                    broadDefs/sectorDefs from the registry instead of hardcoding.
 *   perfBroad      — true if this entry should be grouped in the Performance tab's
 *                    "Broad" section even though category !== 'Broad'/'Index'
 *                    (currently: Bank Nifty)
 */
export const INDEX_REGISTRY = [
  // ── Benchmark ──────────────────────────────────────────────────────────────
  { id:'nifty50',    label:'NIFTY 50',    kiteSymbol:'NIFTY 50',    category:'Index',  inRRG:false, kiteShortLabel:'NIFTY50'    },
  { id:'sensex',     label:'Sensex',      kiteSymbol:'SENSEX',      category:'Index',  inRRG:false, kiteShortLabel:'SENSEX'     },
  { id:'indiavix',   label:'India VIX',   kiteSymbol:'INDIA VIX',   category:'Index',  inRRG:false, kiteShortLabel:'INDIAVIX'   },
  // ── Broad market ───────────────────────────────────────────────────────────
  { id:'niftynext50', label:'Nifty Next 50',    kiteSymbol:'NIFTY NEXT 50',    category:'Broad', inRRG:false, kiteShortLabel:'NIFTYNEXT50' },
  { id:'nifty100',    label:'Nifty 100',         kiteSymbol:'NIFTY 100',        category:'Broad', inRRG:false, kiteShortLabel:'NIFTY100'    },
  { id:'nifty200',    label:'Nifty 200',         kiteSymbol:'NIFTY 200',        category:'Broad', inRRG:false, kiteShortLabel:'NIFTY200'    },
  { id:'nifty500',    label:'Nifty 500',         kiteSymbol:'NIFTY 500',        category:'Broad', inRRG:false, kiteShortLabel:'NIFTY500'    },
  { id:'midcap100',   label:'Nifty Midcap 100',  kiteSymbol:'NIFTY MIDCAP 100', category:'Broad', inRRG:false, kiteShortLabel:'MIDCAP100'   },
  { id:'midcap150',   label:'Nifty Midcap 150',  kiteSymbol:'NIFTY MIDCAP 150', category:'Broad', inRRG:false, kiteShortLabel:'MIDCAP150'   },
  { id:'midcap50',    label:'Nifty Midcap 50',   kiteSymbol:'NIFTY MIDCAP 50',  category:'Broad', inRRG:false, kiteShortLabel:'MIDCAP50'    },
  { id:'smallcap100', label:'Nifty Smallcap 100', kiteSymbol:'NIFTY SMLCAP 100', category:'Broad', inRRG:false, kiteShortLabel:'SMALLCAP100' },
  { id:'smallcap250', label:'Nippon SC250 MF',    kiteSymbol:'NIFTY SMLCAP 250', category:'Broad', inRRG:false, kiteShortLabel:'SMALLCAP250' },
  { id:'totalmkt',    label:'Angel TotalMkt ETF', kiteSymbol:'NIFTY TOTAL MKT',  category:'Broad', inRRG:false, kiteShortLabel:'TOTALMKT'    },
  // ── Core sector indices ────────────────────────────────────────────────────
  // perfBroad:true → appears in "Broad" section of Performance tab (alongside NIFTY 50, Sensex)
  { id:'banknifty',     label:'Bank Nifty',        kiteSymbol:'NIFTY BANK',        category:'Sector', inRRG:true, rrgTicker:'BANKNIFTY',    sectorName:'Bank Nifty',           kiteShortLabel:'BANKNIFTY',     perfBroad:true },
  { id:'niftyit',       label:'NIFTY IT',           kiteSymbol:'NIFTY IT',          category:'Sector', inRRG:true, rrgTicker:'NIFTYIT',       sectorName:'IT',                   kiteShortLabel:'NIFTYIT'        },
  { id:'niftypharma',   label:'NIFTY Pharma',       kiteSymbol:'NIFTY PHARMA',      category:'Sector', inRRG:true, rrgTicker:'NIFTYPHARMA',   sectorName:'Pharmaceuticals',      kiteShortLabel:'NIFTYPHARMA'    },
  { id:'niftyfmcg',     label:'NIFTY FMCG',         kiteSymbol:'NIFTY FMCG',        category:'Sector', inRRG:true, rrgTicker:'NIFTYFMCG',     sectorName:'FMCG',                 kiteShortLabel:'NIFTYFMCG'      },
  { id:'niftymetal',    label:'NIFTY Metal',        kiteSymbol:'NIFTY METAL',       category:'Sector', inRRG:true, rrgTicker:'NIFTYMETAL',    sectorName:'Metals',               kiteShortLabel:'NIFTYMETAL'     },
  { id:'niftyauto',     label:'NIFTY Auto',         kiteSymbol:'NIFTY AUTO',        category:'Sector', inRRG:true, rrgTicker:'NIFTYAUTO',     sectorName:'Automobile',           kiteShortLabel:'NIFTYAUTO'      },
  { id:'niftyrealty',   label:'NIFTY Realty',       kiteSymbol:'NIFTY REALTY',      category:'Sector', inRRG:true, rrgTicker:'NIFTYREALTY',   sectorName:'Real Estate',          kiteShortLabel:'NIFTYREALTY'    },
  { id:'niftyenergy',   label:'NIFTY Energy',       kiteSymbol:'NIFTY ENERGY',      category:'Sector', inRRG:true, rrgTicker:'NIFTYENERGY',   sectorName:'Energy',               kiteShortLabel:'NIFTYENERGY'    },
  { id:'niftyinfra',    label:'NIFTY Infra',        kiteSymbol:'NIFTY INFRA',       category:'Sector', inRRG:true, rrgTicker:'NIFTYINFRA',    sectorName:'Infrastructure',       kiteShortLabel:'NIFTYINFRA'     },
  { id:'niftypsubank',  label:'PSU Bank',           kiteSymbol:'NIFTY PSU BANK',    category:'Sector', inRRG:true, rrgTicker:'NIFTYPSUBANK',  sectorName:'PSU Banks',            kiteShortLabel:'NIFTYPSUBANK'   },
  { id:'niftypvtbank',  label:'Pvt Bank',           kiteSymbol:'NIFTY PVT BANK',    category:'Sector', inRRG:true, rrgTicker:'NIFTYPVTBANK',  sectorName:'Private Banks',        kiteShortLabel:'NIFTYPVTBANK'   },
  { id:'niftyhealth',   label:'Healthcare',         kiteSymbol:'NIFTY HEALTHCARE',  category:'Sector', inRRG:true, rrgTicker:'NIFTYHEALTH',   sectorName:'Healthcare',           kiteShortLabel:'NIFTYHEALTHCARE' },
  { id:'niftyfinsvc',   label:'Fin Services',       kiteSymbol:'NIFTY FIN SERVICE', category:'Sector', inRRG:true, rrgTicker:'FINNIFTY',      sectorName:'Financial Services',   kiteShortLabel:'NIFTYFIN'       },
  { id:'niftymedia',    label:'NIFTY Media',        kiteSymbol:'NIFTY MEDIA',       category:'Sector', inRRG:true, rrgTicker:'NIFTYMEDIA',    sectorName:'Media & Entertainment', kiteShortLabel:'NIFTYMEDIA'    },
  { id:'niftyoilgas',   label:'Oil & Gas',          kiteSymbol:'NIFTY OIL AND GAS', category:'Sector', inRRG:true, rrgTicker:'NIFTYOILGAS',   sectorName:'Oil & Gas',            kiteShortLabel:'NIFTYOILGAS'    },
  { id:'niftyconsumer', label:'Consumer Durables',  kiteSymbol:'NIFTY CONSR DURBL', category:'Sector', inRRG:true, rrgTicker:'NIFTYCONSUMER', sectorName:'Consumer Durables',    kiteShortLabel:'NIFTYCONSUMER'  },
  // ── Thematic indices — kiteShortLabel added so Dashboard heatmap can display them ──
  // ── Commodity / FX / Crypto / Debt assets (used by SidePanel resolveId) ──────
  { id:'gold',    label:'Gold',         kiteSymbol:'GC=F',    category:'Commodity', inRRG:false },
  { id:'silver',  label:'Silver',       kiteSymbol:'SI=F',    category:'Commodity', inRRG:false },
  { id:'crude',   label:'Crude Oil',    kiteSymbol:'CL=F',    category:'Commodity', inRRG:false },
  { id:'natgas',  label:'Natural Gas',  kiteSymbol:'NG=F',    category:'Commodity', inRRG:false },
  { id:'usdinr',  label:'USD/INR',      kiteSymbol:'INR=X',   category:'Currency',  inRRG:false },
  { id:'bitcoin', label:'Bitcoin',      kiteSymbol:'BTC-USD', category:'Crypto',    inRRG:false },
  { id:'gsec10',  label:'G-Sec 10Y',    kiteSymbol:'GSEC10IETF.NS', category:'Debt', inRRG:false },
  { id:'gsec5',   label:'G-Sec 5Y',     kiteSymbol:'GILT5YBEES.NS', category:'Debt', inRRG:false },
  // ── Thematic indices — kiteShortLabel added so Dashboard heatmap can display them ──
  { id:'niftycommodities', label:'NIFTY Commodities', kiteSymbol:'NIFTY COMMODITIES', category:'Thematic', inRRG:true, rrgTicker:'NIFTYCMDT', sectorName:'Commodities', kiteShortLabel:'NIFTYCMDT'  },
  { id:'niftycpse',        label:'NIFTY CPSE',        kiteSymbol:'NIFTY CPSE',        category:'Thematic', inRRG:true, rrgTicker:'NIFTYCPSE',  sectorName:'CPSE',        kiteShortLabel:'NIFTYCPSE'  },
  { id:'niftypse',         label:'NIFTY PSE',         kiteSymbol:'NIFTY PSE',         category:'Thematic', inRRG:true, rrgTicker:'NIFTYPSE',   sectorName:'PSE',         kiteShortLabel:'NIFTYPSE'   },
  { id:'niftymnc',         label:'NIFTY MNC',         kiteSymbol:'NIFTY MNC',         category:'Thematic', inRRG:true, rrgTicker:'NIFTYMNC',   sectorName:'MNC',         kiteShortLabel:'NIFTYMNC'   },
  { id:'niftyserv',        label:'Services Sector',   kiteSymbol:'NIFTY SERV SECTOR', category:'Thematic', inRRG:true, rrgTicker:'NIFTYSERV',  sectorName:'Services',    kiteShortLabel:'NIFTYSERV'  },
];

// ── Auto-generated lookup maps — never edit these, edit INDEX_REGISTRY above ──

/**
 * kiteSymbol → label  (for SidePanel full-name display)
 * e.g. 'NIFTY BANK' → 'Bank Nifty'
 */
export const KITE_TO_LABEL = Object.fromEntries(
  INDEX_REGISTRY.map(e => [e.kiteSymbol, e.label])
);

/**
 * kiteSymbol → display name for PerformanceTab / Dashboard heatmap.
 * Uses sectorName if defined (e.g. 'Automobile' for 'NIFTY AUTO'),
 * otherwise falls back to label (e.g. 'Nifty Midcap 100' for 'NIFTY MIDCAP 100').
 * Single point of truth — change label/sectorName above, all tabs update.
 */
export const KITE_TO_DISPLAY = Object.fromEntries(
  INDEX_REGISTRY.map(e => [e.kiteSymbol, e.sectorName || e.label])
);

/**
 * kiteShortLabel → display name  (for Dashboard broadDefs / sectorDefs)
 * kiteShortLabel matches the key used in backend KITE_INDICES
 * e.g. 'SMALLCAP250' → 'Nippon SC250 MF', 'NIFTYAUTO' → 'Automobile'
 */
export const KITE_SHORT_TO_DISPLAY = Object.fromEntries(
  INDEX_REGISTRY
    .filter(e => e.kiteShortLabel)
    .map(e => [e.kiteShortLabel, e.sectorName || e.label])
);

/**
 * kiteShortLabel → kiteSymbol  (for DISPLAY_TO_KITE_SYMBOL auto-derivation)
 * e.g. 'TOTALMKT' → 'NIFTY TOTAL MKT'
 */
export const KITE_SHORT_TO_KITE = Object.fromEntries(
  INDEX_REGISTRY
    .filter(e => e.kiteShortLabel)
    .map(e => [e.kiteShortLabel, e.kiteSymbol])
);

/**
 * kiteSymbol (normalised: upper, no spaces) → label
 * Handles variants like 'NIFTYBANK' → 'Bank Nifty'
 */
const _normToLabel = {};
INDEX_REGISTRY.forEach(e => {
  _normToLabel[e.kiteSymbol.toUpperCase().replace(/\s+/g, '')] = e.label;
  if (e.rrgTicker) _normToLabel[e.rrgTicker.toUpperCase()] = e.label;
  _normToLabel[e.id.toUpperCase()] = e.label;
});

/**
 * Resolve any symbol variant → label string.
 * Handles: kiteSymbol, rrgTicker, id, normalised (no-space, upper) forms.
 */
export function resolveLabel(name) {
  if (!name) return null;
  return KITE_TO_LABEL[name]
    || _normToLabel[name.toUpperCase().replace(/\s+/g, '')]
    || null;
}

/**
 * Resolve any symbol variant → backend RS asset id.
 * Used by SidePanel to find the matching RS data entry.
 */
const _normToId = {};
INDEX_REGISTRY.forEach(e => {
  _normToId[e.id] = e.id;
  _normToId[e.kiteSymbol.toLowerCase().replace(/\s+/g, '')] = e.id;
  if (e.rrgTicker) _normToId[e.rrgTicker.toLowerCase()] = e.id;
});

export function resolveId(name) {
  if (!name) return null;
  // Strip "YF:" prefix used by Dashboard commodity cards (e.g. "YF:SI=F" → "SI=F")
  const stripped = name.replace(/^YF:/i, '');
  const n = stripped.toLowerCase().replace(/\s+/g, '');
  return _normToId[n] ?? null;
}

/**
 * RRG ticker → kiteSymbol (for sector rotation click → SidePanel)
 */
export const RRG_TICKER_TO_KITE = Object.fromEntries(
  INDEX_REGISTRY
    .filter(e => e.inRRG && e.rrgTicker)
    .map(e => [e.rrgTicker, e.kiteSymbol])
);

/**
 * TICKER_MAP for SectorRotationTab: id → rrgTicker
 */
export const RRG_TICKER_MAP = Object.fromEntries(
  INDEX_REGISTRY
    .filter(e => e.inRRG)
    .map(e => [e.id, e.rrgTicker || e.kiteSymbol.replace(/\s+/g, '')])
);
